import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, AlertTriangle, Loader2, ShoppingBag, Store, Info, Phone, Mail, MapPin, Home as HomeIcon, RefreshCw, Search } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/components/ui/use-toast";
import { sendAllOrderNotifications } from "@/utils/orderNotifications";

// Define interfaces for better type safety
interface OrderItemFromJSON {
  id?: string; // The items in JSON might not have a DB id, use index if needed for key
  name: string; 
  quantity: number;
  price: number;
  size?: string; 
}

interface StoreProfile {
  id: string;
  store_name?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  phone_number?: string; // Assuming field name
  email?: string; // Assuming field name
  // Add other relevant store fields
}

interface AddressObject {
  street?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
}

interface OrderData {
  id: string;
  order_number?: string;
  total_amount: number;
  customer_email?: string;
  delivery_address?: string; // This is likely a JSON string
  delivery_date?: string;
  delivery_type?: string; // e.g. 'delivery', 'pickup'
  special_instructions?: string;
  florist_id: StoreProfile | null; // Can be object or null if not expanded/found
  items: OrderItemFromJSON[]; // CHANGED: Expecting items directly from the JSONB column
  order_items?: any[]; // Keep for safety but we won't primarily use this if items is populated
  notes?: string; // For legacy deliveryDetails parsing
  recipient_first_name?: string;
  recipient_last_name?: string;
  recipient_phone?: string;
  buyer_first_name?: string;
  delivery_fee: number;
}

interface OrderItemData {
  name?: string; 
  title?: string; 
  quantity: number; 
  price: number; 
  main_image_url?: string; 
  product_name?: string; 
  price_at_purchase?: number; 
  size?: string;
}

interface StoreProfileData { 
  id?: string;
  store_name?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  phone?: string;
  email?: string;
  storefront_image_url?: string; // Primary image based on ProductDetail.tsx usage
  cover_image_url?: string;    // Secondary image
}

interface DisplayOrderData {
  id: string; 
  order_number?: string;
  total_amount?: number;
  buyer_email?: string; 
  customer_email?: string;
  customer_id?: string;
  is_delivery?: boolean;
  delivery_address?: string; 
  delivery_date?: string;
  delivery_time?: string;
  delivery_notes?: string; 
  special_instructions?: string;
  items?: OrderItemData[]; 
  status?: string;
  delivery_fee?: number;
  delivery_type?: string;
  is_same_day?: boolean;
  store_profiles?: StoreProfileData | null; 
  florist_id?: string; 
  stripe_payment_intent_id?: string; 
  payment_status?: string;
  buyer_first_name?: string;
  buyer_last_name?: string;
  recipient_first_name?: string;
  recipient_last_name?: string;
  recipient_phone?: string;
  gift_message?: string;
  is_gift?: boolean;
  commission_amount?: number;
  delivery_coordinates?: { lat: number; lng: number } | string | null;
}

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: authUser, loading: authLoading } = useAuth();
  const { clear } = useCart();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<DisplayOrderData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notificationSent, setNotificationSent] = useState(false);
  const cartCleared = useRef(false);
  const notificationStarted = useRef(false);

  const stripeSessionId = searchParams.get("session_id"); 
  const internalOrderIdFromUrl = searchParams.get("order_id");

  console.log('[OrderConfirmation Mount] stripeSessionId from URL:', stripeSessionId);
  console.log('[OrderConfirmation Mount] internalOrderIdFromUrl from URL:', internalOrderIdFromUrl);
  
  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount == null) return "$0.00";
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };
  
  const processStripePaymentConfirmation = useCallback(async () => {
    if (!stripeSessionId) { 
        setError("Stripe session ID is missing. Cannot confirm payment.");
        setLoading(false);
        return;
    }
    
    if (authLoading) {
        console.log("[OrderConfirmation] Waiting for auth to resolve before processing Stripe confirmation...");
        return;
      }
      
      setLoading(true);
    setError(null);
    console.log(`[OrderConfirmation] Processing Stripe confirmation. Session ID: ${stripeSessionId}, URL Order ID: ${internalOrderIdFromUrl}`);

    try {
      const { data: sessionDetailsResponse, error: sessionDetailsError } = await supabase.functions.invoke(
        'get-stripe-checkout-session-details',
        { body: { session_id: stripeSessionId } }
        );

      console.log("[OrderConfirmation] Stripe Session Details Response:", sessionDetailsResponse);
      if (sessionDetailsError || !sessionDetailsResponse || sessionDetailsResponse.error) {
        console.error("[OrderConfirmation] Error fetching Stripe session details:", sessionDetailsError, sessionDetailsResponse?.error);
        throw new Error(sessionDetailsResponse?.error || sessionDetailsError?.message || "Failed to retrieve payment details from Stripe.");
      }
      
      const { paymentIntentId, paymentIntentStatus, metadata } = sessionDetailsResponse;
      console.log('[OrderConfirmation process] Extracted metadata from Stripe session:', metadata);
      console.log('[OrderConfirmation process] internalOrderIdFromUrl (available in this scope):', internalOrderIdFromUrl);

      const confirmedOrderId = metadata?.marketbloom_order_id || internalOrderIdFromUrl;
      console.log('[OrderConfirmation process] confirmedOrderId determined as:', confirmedOrderId);

      if (!confirmedOrderId) {
        console.error("[OrderConfirmation process] CRITICAL: confirmedOrderId is null or undefined.", { metadata, internalOrderIdFromUrl });
        throw new Error("Internal Order ID could not be determined from Stripe session or URL.");
      }

      const newOrderStatus = "awaiting_florist_confirmation";
      console.log(`[OrderConfirmation] Updating order ${confirmedOrderId} with PI: ${paymentIntentId}, Status: ${newOrderStatus}`);
      
      const { data: updateOrderResponse, error: updateOrderError } = await supabase.functions.invoke(
        'update-order-after-stripe-auth',
        {
          body: {
            order_id: confirmedOrderId,
            payment_intent_id: paymentIntentId,
            new_order_status: newOrderStatus,
          }
        }
      );

      if (updateOrderError || !updateOrderResponse?.success) {
        console.error("[OrderConfirmation] Error updating order with payment details:", updateOrderError, updateOrderResponse?.error);
        setError("Payment authorized, but failed to update order details in our system. Please contact support with your Order ID: " + confirmedOrderId);
        setLoading(false);
        return;
      }
      console.log("[OrderConfirmation] Order updated successfully with Stripe details. Fetching final order data...");

      const selectString = `
        id, order_number, total_amount, customer_email, buyer_first_name, buyer_last_name, 
        is_delivery, delivery_address, delivery_date, delivery_time, delivery_type, 
        delivery_notes, special_instructions, status, delivery_fee, is_same_day, 
        stripe_payment_intent_id, items, payment_status, 
        recipient_first_name, recipient_last_name, recipient_phone, gift_message, is_gift,
        florist_id, customer_id, commission_amount, delivery_coordinates, 
        store_profiles!inner(id, store_name, suburb, state, postcode, phone, email, storefront_image_url, cover_image_url)
      `;

      const { data: finalOrderDataResultArray, error: finalOrderError } = await supabase
        .from('orders')
        .select(selectString)
        .eq('id', confirmedOrderId as any);

      if (finalOrderError) {
        console.error('[OrderConfirmation] Error fetching final order details:', finalOrderError);
        throw new Error("Order details could not be loaded after payment confirmation (fetch error).");
      }

      console.log(`[OrderConfirmation] Number of rows returned for order ID ${confirmedOrderId}: ${finalOrderDataResultArray?.length ?? 0}`);

      if (!finalOrderDataResultArray || finalOrderDataResultArray.length === 0) {
        console.error('[OrderConfirmation] No data returned for final order details for order ID:', confirmedOrderId);
        // It's possible RLS is preventing access, or the order truly isn't found/ready.
        throw new Error("Order details not found after payment confirmation. This could be due to access restrictions (RLS) or the order not being available yet.");
      }

      if (finalOrderDataResultArray.length > 1) {
        console.warn('[OrderConfirmation] Multiple orders returned for ID:', confirmedOrderId, finalOrderDataResultArray);
        // Take the first one, but this indicates a potential issue with duplicate IDs or query logic
      }

      const finalOrderDataResult = finalOrderDataResultArray[0]; // Use the first result
        
      console.log("[OrderConfirmation] Raw finalOrderDataResult:", finalOrderDataResult);

      const typedFinalOrderData = { ...((finalOrderDataResult as any) || {}) } as unknown as DisplayOrderData;
      
      if (finalOrderDataResult && typeof finalOrderDataResult === 'object' && 'items' in finalOrderDataResult && Array.isArray(finalOrderDataResult.items)) {
        typedFinalOrderData.items = (finalOrderDataResult.items as any[]).map((dbItem: any) => ({
          name: dbItem.name || dbItem.title || 'Unknown Item',
          quantity: dbItem.quantity || 0,
          price: dbItem.price || 0, 
          main_image_url: dbItem.main_image_url,
          product_name: dbItem.product_name, 
          price_at_purchase: dbItem.price_at_purchase,
          size: dbItem.size,
        }));
      } else {
        typedFinalOrderData.items = []; 
        console.warn("[OrderConfirmation] finalOrderDataResult.items was not a valid array. Items set to [].", finalOrderDataResult);
      }

      if (finalOrderDataResult && typeof finalOrderDataResult === 'object' && 'store_profiles' in finalOrderDataResult && finalOrderDataResult.store_profiles) {
        typedFinalOrderData.store_profiles = finalOrderDataResult.store_profiles as StoreProfileData;
      } else {
        typedFinalOrderData.store_profiles = null;
        console.warn("[OrderConfirmation] finalOrderDataResult.store_profiles was not valid or present. Set to null.", finalOrderDataResult);
      }

      console.log("[OrderConfirmation] Processed typedFinalOrderData for display:", typedFinalOrderData);
      setOrder(typedFinalOrderData);
        setError(null); 

      // Check if notifications have already been sent
      if (!notificationSent && !notificationStarted.current) {
        // First check if notifications already exist for this order
        const { data: existingNotifications, error: notificationCheckError } = await supabase
          .from('order_notifications')
          .select('id')
          .eq('order_id', confirmedOrderId as any)
          .eq('type', 'new_order' as any)
          .limit(1);
        
        if (notificationCheckError) {
          console.error("[OrderConfirmation] Error checking existing notifications:", notificationCheckError);
        }
        
        // Only send notifications if they haven't been sent yet
        if (!existingNotifications || existingNotifications.length === 0) {
          console.log("[OrderConfirmation] Sending order notifications...");
          notificationStarted.current = true; // Mark as started immediately
          
          // Send notifications via manual function call since webhook may not be configured
          console.log("[OrderConfirmation] Sending notifications manually - webhook may not be working");
          
          // Send customer confirmation email
          supabase.functions.invoke('send-brevo-notification', {
            body: {
              type: 'PAYMENT_CONFIRMATION',
              email: typedFinalOrderData.customer_email || typedFinalOrderData.buyer_email,
              data: {
                customerName: typedFinalOrderData.buyer_first_name || 'there',
                orderNumber: typedFinalOrderData.order_number,
                orderId: typedFinalOrderData.id,
                amount: typedFinalOrderData.total_amount,
                deliveryDate: typedFinalOrderData.delivery_date,
                isDelivery: typedFinalOrderData.is_delivery,
                deliveryAddress: typedFinalOrderData.delivery_address,
                storeName: typedFinalOrderData.store_profiles?.store_name || 'MarketBloom'
              }
            }
          }).then(({ error: customerEmailError }) => {
            if (customerEmailError) {
              console.error("[OrderConfirmation] Error sending customer email:", customerEmailError);
            } else {
              console.log("[OrderConfirmation] Customer confirmation email sent successfully");
            }
          });

          // Send florist notification email
          if (typedFinalOrderData.store_profiles?.email) {
            supabase.functions.invoke('send-brevo-notification', {
              body: {
                type: 'NEW_ORDER_FLORIST',
                email: typedFinalOrderData.store_profiles.email,
                data: {
                  storeName: typedFinalOrderData.store_profiles.store_name,
                  orderNumber: typedFinalOrderData.order_number,
                  orderId: typedFinalOrderData.id,
                  amount: typedFinalOrderData.total_amount,
                  deliveryDate: typedFinalOrderData.delivery_date,
                  isDelivery: typedFinalOrderData.is_delivery,
                  deliveryAddress: typedFinalOrderData.delivery_address,
                  customerName: typedFinalOrderData.buyer_first_name || 'Customer',
                  customerEmail: typedFinalOrderData.customer_email || typedFinalOrderData.buyer_email,
                  customerPhone: 'Will be provided in SMS section below'
                }
              }
            }).then(({ error: floristEmailError }) => {
              if (floristEmailError) {
                console.error("[OrderConfirmation] Error sending florist email:", floristEmailError);
              } else {
                console.log("[OrderConfirmation] Florist notification email sent successfully");
              }
            });

            // Send florist SMS notification
            if (typedFinalOrderData.store_profiles.phone) {
              // Get customer phone from database for SMS notifications
              supabase
                .from('orders')
                .select('customer_phone, buyer_phone')
                .eq('id', confirmedOrderId as any)
                .single()
                .then(({ data: phoneData, error: phoneError }) => {
                  if (phoneError || !phoneData) {
                    console.error("[OrderConfirmation] Error fetching phone data:", phoneError);
                    return;
                  }
                  const customerPhone = (phoneData as any).customer_phone || (phoneData as any).buyer_phone;
                  
                  supabase.functions.invoke('send-brevo-notification', {
                    body: {
                      type: 'NEW_ORDER_FLORIST',
                      phone: typedFinalOrderData.store_profiles!.phone,
                      notificationType: 'sms',
                      data: {
                        storeName: typedFinalOrderData.store_profiles!.store_name,
                        orderNumber: typedFinalOrderData.order_number,
                        orderId: typedFinalOrderData.id,
                        amount: typedFinalOrderData.total_amount,
                        deliveryDate: typedFinalOrderData.delivery_date,
                        isDelivery: typedFinalOrderData.is_delivery,
                        deliveryAddress: typedFinalOrderData.delivery_address,
                        customerName: typedFinalOrderData.buyer_first_name || 'Customer',
                        customerEmail: typedFinalOrderData.customer_email || typedFinalOrderData.buyer_email,
                        customerPhone: customerPhone || 'Not provided'
                      }
                    }
                  }).then(({ error: floristSmsError }) => {
                    if (floristSmsError) {
                      console.error("[OrderConfirmation] Error sending florist SMS:", floristSmsError);
                    } else {
                      console.log("[OrderConfirmation] Florist SMS notification sent successfully");
                    }
                  });
                });
            }
          }
          
          // Send immediate SMS notification if customer opted in
          console.log("[OrderConfirmation] Checking for SMS opt-in...");
          supabase
            .from('orders')
            .select('sms_opt_in, customer_phone, buyer_phone, customer_name, order_number, total_amount, delivery_date, is_delivery, delivery_address, store_profiles!inner(store_name)')
            .eq('id', confirmedOrderId as any)
            .single()
            .then(({ data: smsOrderData, error: smsError }) => {
              if (smsError || !smsOrderData) {
                console.error("[OrderConfirmation] Error fetching SMS data:", smsError);
                return;
              }
              
              const orderData = smsOrderData as any;
              const customerPhone = orderData.customer_phone || orderData.buyer_phone;
              console.log("[OrderConfirmation] SMS Debug - Full order data:", {
                orderId: confirmedOrderId,
                smsOptIn: orderData.sms_opt_in,
                customerPhone: customerPhone,
                phoneFields: {
                  customer_phone: orderData.customer_phone,
                  buyer_phone: orderData.buyer_phone
                },
                orderNumber: orderData.order_number
              });
              
              if (orderData.sms_opt_in && customerPhone) {
                console.log("[OrderConfirmation] ✅ SENDING SMS - opt-in:", orderData.sms_opt_in, "phone:", customerPhone);
                
                supabase.functions.invoke('send-brevo-notification', {
                  body: {
                    type: 'ORDER_CONFIRMED',
                    phone: customerPhone,
                    notificationType: 'sms',
                    data: {
                      orderNumber: orderData.order_number,
                      orderId: confirmedOrderId,
                      amount: orderData.total_amount,
                      deliveryDate: orderData.delivery_date,
                      isDelivery: orderData.is_delivery,
                      deliveryAddress: orderData.delivery_address,
                      storeName: orderData.store_profiles?.store_name
                    }
                  }
                }).then(({ data: smsResponse, error: smsNotificationError }) => {
                  if (smsNotificationError) {
                    console.error("[OrderConfirmation] ❌ SMS ERROR:", smsNotificationError);
                  } else {
                    console.log("[OrderConfirmation] ✅ SMS SUCCESS:", smsResponse);
                  }
                });
              } else {
                console.log("[OrderConfirmation] ❌ SMS NOT SENT - Requirements not met:");
                console.log("- SMS opt-in:", orderData.sms_opt_in, "(should be true)");
                console.log("- Customer phone:", customerPhone, "(should exist)");
                
                // Send SMS anyway for testing if phone exists but opt-in might be false
                if (customerPhone && !orderData.sms_opt_in) {
                  console.log("[OrderConfirmation] 🧪 TESTING: Attempting to send SMS anyway since phone exists...");
                  
                  supabase.functions.invoke('send-brevo-notification', {
                    body: {
                      type: 'ORDER_CONFIRMED',
                      phone: customerPhone,
                      notificationType: 'sms',
                      data: {
                        orderNumber: orderData.order_number,
                        orderId: confirmedOrderId,
                        amount: orderData.total_amount,
                        deliveryDate: orderData.delivery_date,
                        isDelivery: orderData.is_delivery,
                                                 deliveryAddress: orderData.delivery_address,
                         storeName: orderData.store_profiles?.store_name
                      }
                    }
                  }).then(({ data: testSmsResponse, error: testSmsError }) => {
                    if (testSmsError) {
                      console.error("[OrderConfirmation] 🧪 TEST SMS ERROR:", testSmsError);
                    } else {
                      console.log("[OrderConfirmation] 🧪 TEST SMS SUCCESS:", testSmsResponse);
                    }
                  });
                }
              }
            });
          
          setNotificationSent(true);
        } else {
          console.log("[OrderConfirmation] Notifications already exist for this order, skipping.");
          setNotificationSent(true);
        }
      }

      if (!cartCleared.current) {
        clear();
        cartCleared.current = true;
        console.log("[OrderConfirmation] Cart cleared.");
      }

      } catch (err: any) {
      console.error("[OrderConfirmation] Error in processStripePaymentConfirmation:", err);
      setError(err.message || "An error occurred while confirming your payment. Please contact support.");
      } finally {
      setLoading(false);
    }
  }, [stripeSessionId, internalOrderIdFromUrl, toast, clear, notificationSent, navigate, authLoading]);
  
  useEffect(() => {
    if (authLoading) {
      console.log("[OrderConfirmation Mount] Waiting for authentication to load...");
      setLoading(true);
      return;
    }

    console.log("[OrderConfirmation Mount] Auth loaded. User:", authUser);
    if (stripeSessionId) {
      // Process for both authenticated and guest users
      processStripePaymentConfirmation();
      /*
      if (authUser) {
        processStripePaymentConfirmation();
      } else {
        setError("You must be logged in to confirm this order. Please log in and try again or check your email.");
        setLoading(false);
      }
      */
    } else if (internalOrderIdFromUrl) {
      setLoading(true);
      setError(null);
      console.log(`[OrderConfirmation] No Stripe session_id, attempting to load order ${internalOrderIdFromUrl} directly.`);
      
      const selectStringDirectLoad = `
        id, order_number, total_amount, customer_email, buyer_first_name, buyer_last_name, 
        is_delivery, delivery_address, delivery_date, delivery_time, delivery_type, 
        delivery_notes, special_instructions, status, delivery_fee, is_same_day, 
        stripe_payment_intent_id, items, payment_status, 
        recipient_first_name, recipient_last_name, recipient_phone, gift_message, is_gift,
        florist_id, customer_id, commission_amount, delivery_coordinates, 
        store_profiles!inner(id, store_name, suburb, state, postcode, phone, email, storefront_image_url, cover_image_url)
      `;

      supabase.from('orders')
        .select(selectStringDirectLoad)
        .eq('id', internalOrderIdFromUrl as any)
        .single()
        .then(({ data: orderDataResult, error: orderFetchError }) => {
          if (orderFetchError) {
            setError("Could not load order details. " + (orderFetchError?.message || ''));
            console.error("Error loading order directly (fetch error):", orderFetchError);
            setOrder(null);
            setLoading(false);
            return;
          }
          if (!orderDataResult) {
            setError("Could not load order details (no data).");
            console.error("Error loading order directly: No data for ID", internalOrderIdFromUrl);
            setOrder(null);
            setLoading(false);
            return;
          }

          console.log("[OrderConfirmation Direct Load] Raw orderDataResult:", orderDataResult);
          const typedData = { ...((orderDataResult as any) || {}) } as unknown as DisplayOrderData;
            
          if (orderDataResult && typeof orderDataResult === 'object' && 'items' in orderDataResult && Array.isArray(orderDataResult.items)) {
              typedData.items = (orderDataResult.items as any[]).map((dbItem: any) => ({
                name: dbItem.name || dbItem.title || 'Unknown Item',
                quantity: dbItem.quantity || 0,
                price: dbItem.price || 0,
                main_image_url: dbItem.main_image_url,
                product_name: dbItem.product_name,
                price_at_purchase: dbItem.price_at_purchase,
                size: dbItem.size,
              }));
          } else {
              typedData.items = [];
              console.warn("[OrderConfirmation Direct Load] orderDataResult.items was not valid. Items set to [].", orderDataResult);
          }

          if (orderDataResult && typeof orderDataResult === 'object' && 'store_profiles' in orderDataResult && orderDataResult.store_profiles) {
            typedData.store_profiles = orderDataResult.store_profiles as StoreProfileData;
          } else {
            typedData.store_profiles = null;
            console.warn("[OrderConfirmation Direct Load] orderDataResult.store_profiles was not valid or present. Set to null.", orderDataResult);
          }

          console.log("[OrderConfirmation Direct Load] Processed typedData for display:", typedData);
          setOrder(typedData);
          setLoading(false);
        });
    } else {
      setError("Order information is missing from the URL. No session_id or order_id found.");
      setLoading(false);
    }
  }, [stripeSessionId, internalOrderIdFromUrl, processStripePaymentConfirmation, authUser, authLoading]);
  
  // Parse delivery_address JSON string
  let parsedDeliveryAddress: AddressObject | null = null;
  if (order?.delivery_address && typeof order.delivery_address === 'string') {
    try {
      parsedDeliveryAddress = JSON.parse(order.delivery_address);
    } catch (e) {
      console.error("Failed to parse delivery_address JSON:", e);
      // Keep parsedDeliveryAddress as null if parsing fails
    }
  }

  const finalDeliveryInfo = {
    addressObj: parsedDeliveryAddress,
    date: order?.delivery_date,
    type: order?.delivery_type,
    instructions: order?.special_instructions || order?.delivery_notes,
    recipientName: `${order?.recipient_first_name || ''} ${order?.recipient_last_name || ''}`.trim(),
    recipientPhone: order?.recipient_phone
  };

  const storeDetails = order?.store_profiles;

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <main className="container mx-auto px-4 py-8">

        <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
          <CardHeader className="text-center bg-[#967F81] text-white p-6">
            <CheckCircle className="h-16 w-16 mx-auto mb-3" />
            <CardTitle className="text-3xl font-semibold">
              Order Confirmed!
            </CardTitle>
            {order?.order_number && (
              <p className="text-md mt-1 opacity-90">Order # {order.order_number}</p>
            )}
          </CardHeader>
            
          <CardContent className="p-6 space-y-6">
            {loading && (
              <div className="text-center py-10">
                <Loader2 className="h-10 w-10 animate-spin text-brand-primary mx-auto mb-3" />
                <p className="text-md text-gray-500">Loading your order details...</p>
              </div>
            )}
            {error && !loading && (
              <div className="bg-red-50 border-l-4 border-red-400 text-red-700 p-4 rounded-md">
                <div className="flex items-center mb-1">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  <p className="font-medium">There was a problem</p>
                </div>
                <p className="text-sm">{error}</p>
              </div>
            )}
            {!loading && !error && !order && (
                <div className="text-center py-10">
                     <p className="text-lg text-gray-600">Could not load order details at this time.</p>
                </div>
            )}
            {!loading && !error && order && (
              <>
                <div className="text-center">
                  <p className="text-lg text-gray-700 mb-1">Thank you for your order, {order.buyer_first_name || order.customer_email || 'Valued Customer'}!</p>
                  {order.status === 'awaiting_florist_confirmation' ? (
                    <p className="text-sm text-gray-500 mb-4">Your payment has been authorized and your order is awaiting confirmation from the florist.</p>
                  ) : (
                    <p className="text-sm text-gray-500 mb-4">Your beautiful flowers are being prepared. Current status: {order.status?.replace(/_/g, ' ') || 'Processing'}</p>
                  )}
                </div>

                <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
                  <h3 className="text-lg font-semibold text-blue-700 mb-2 flex items-center">
                    <Info size={20} className="mr-2" /> What Happens Next?
                  </h3>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 pl-2">
                    <li><span className="font-medium">Payment Authorized:</span> Your payment details have been successfully authorized.</li>
                    <li>Your order has been sent to <strong>{storeDetails?.store_name || 'the florist'}</strong> and is awaiting their confirmation.</li>
                    <li>Once confirmed by the florist, your payment will be captured and they will begin preparing your order.</li>
                    {order.delivery_type === 'delivery' && <li>You'll receive further updates via email regarding delivery.</li>}
                    {order.delivery_type === 'pickup' && <li>You'll be notified when your order is ready for pickup after florist confirmation.</li>}
                  </ul>
                </div>
                
                <Separator className="my-6" />

                <div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-3 flex items-center">
                    <ShoppingBag size={22} className="mr-2 text-brand-primary" /> Order Summary
                  </h3>
                  <div className="space-y-2">
                    {order.items && order.items.length > 0 ? (
                      order.items.map((item, index) => (
                        <div key={item.product_name || item.name || `item-${index}`} className="flex justify-between items-start p-3 bg-gray-50 rounded-md">
                          <div>
                            <p className="font-medium text-gray-800">{item.name || item.title || 'Item'}</p>
                            {item.size && <p className="text-xs text-gray-500">Size: {item.size}</p>}
                          </div>
                          <div className="text-right flex-shrink-0 ml-4">
                            <p className="text-sm text-gray-600">{item.quantity} x {formatCurrency(item.price_at_purchase || item.price)}</p>
                            <p className="font-medium text-gray-800">{formatCurrency(item.quantity * (item.price_at_purchase || item.price))}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">No items found in this order.</p>
                    )}
                  </div>
                  <Separator className="my-3" />
                  <div className="space-y-1 text-right font-medium pr-1">
                    {(typeof order.delivery_fee === 'number' && order.delivery_fee > 0) && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Delivery Fee:</span>
                        <span className="text-gray-700">{formatCurrency(order.delivery_fee)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-md">
                      <span className="text-gray-600">Total Amount:</span>
                      <span className="text-gray-800 font-semibold">{formatCurrency(order.total_amount)}</span>
                    </div>
                    {order.stripe_payment_intent_id && (
                       <div className="flex justify-between mt-1 text-xs">
                         <span className="text-gray-500">Payment ID:</span>
                         <span className="text-gray-500 truncate max-w-[150px]">{order.stripe_payment_intent_id}</span>
                       </div>
                    )}
                  </div>
                </div>

                <Separator className="my-6" />

                <div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-3 flex items-center">
                    <MapPin size={22} className="mr-2 text-brand-primary" /> 
                    {order.delivery_type === 'delivery' ? 'Delivery Information' : 'Pickup Information'}
                  </h3>
                  <div className="bg-gray-50 p-4 rounded-md space-y-1 text-sm text-gray-700">
                    {order.delivery_type === 'delivery' ? (
                      <>
                        <p><strong>To:</strong> {finalDeliveryInfo?.recipientName || 'N/A'} {finalDeliveryInfo?.recipientPhone && `(${finalDeliveryInfo.recipientPhone})`}</p>
                        {finalDeliveryInfo.addressObj ? (
                          <address className="not-italic">
                            <strong>Address:</strong> {finalDeliveryInfo.addressObj.street}<br />
                            {finalDeliveryInfo.addressObj.suburb}, {finalDeliveryInfo.addressObj.state} {finalDeliveryInfo.addressObj.postcode}
                          </address>
                        ) : (
                          <p><strong>Address:</strong> {order.delivery_address || 'N/A'}</p> 
                        )}
                        <p><strong>Date:</strong> {finalDeliveryInfo?.date ? new Date(finalDeliveryInfo.date).toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</p>
                        {(finalDeliveryInfo?.instructions) && <p><strong>Instructions:</strong> {finalDeliveryInfo.instructions}</p>}
                      </>
                    ) : (
                      <>
                        <p><strong>Pickup At:</strong> {storeDetails?.store_name || 'the store'}</p>
                        {storeDetails && <p><strong>Address:</strong> {storeDetails.suburb}, {storeDetails.state} {storeDetails.postcode}</p>}
                        <p><strong>Date:</strong> {finalDeliveryInfo?.date ? new Date(finalDeliveryInfo.date).toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</p>
                        {(finalDeliveryInfo?.instructions) && <p><strong>Instructions:</strong> {finalDeliveryInfo.instructions}</p>}
                      </>
                    )}
                  </div>
                </div>
                
                <Separator className="my-6" />

                {storeDetails && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
                      <Store size={22} className="mr-2 text-brand-primary" /> Ordered From
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-md space-y-3">
                      <p className="text-lg font-medium text-gray-800">{storeDetails.store_name}</p>
                      {storeDetails.suburb && <p className="text-sm text-gray-600">{storeDetails.suburb}, {storeDetails.state} {storeDetails.postcode}</p>}
                      
                      {/* Enhanced Image Display - Side by Side with 1/3 - 2/3 split idea from ProductDetail */}
                      <div className="flex flex-col md:flex-row md:gap-4 mt-3 mb-3">
                        {/* Square-ish Profile Image (Storefront Image) */}
                        {storeDetails.storefront_image_url && (
                          <div className="w-full md:w-1/3 mb-3 md:mb-0 flex-shrink-0">
                            <div className="aspect-square w-full relative rounded-lg overflow-hidden bg-gray-100">
                              <img 
                                src={storeDetails.storefront_image_url} 
                                alt={`${storeDetails.store_name} storefront`} 
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* Wider Cover Image */}
                        {storeDetails.cover_image_url && (
                          <div className="w-full md:flex-1">
                            <div className="aspect-video w-full relative rounded-lg overflow-hidden bg-gray-100"> {/* aspect-video for wider ratio */}
                              <img 
                                src={storeDetails.cover_image_url} 
                                alt={`${storeDetails.store_name} cover`} 
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Display only one image if the other is missing, or a placeholder */} 
                      {/* This simple conditional rendering might need more sophisticated fallback if one is present and other not */} 
                      {!storeDetails.storefront_image_url && !storeDetails.cover_image_url && (
                        <div className="w-full h-32 bg-gray-100 flex items-center justify-center rounded-md text-gray-400 mb-3">
                          No store image available
                        </div>
                      )}

                      {storeDetails.phone && <p className="flex items-center text-sm"><Phone size={15} className="mr-1.5 text-gray-500" /> {storeDetails.phone}</p>}
                      {storeDetails.email && <p className="flex items-center text-sm"><Mail size={15} className="mr-1.5 text-gray-500" /> {storeDetails.email}</p>}
                      <p className="text-xs mt-2 pt-2 border-t border-gray-200">Thank you for supporting local florists!</p>
                    </div>
                  </div>
                )}
                <Separator className="my-6" />

                <div className="text-center text-sm text-gray-500">
                  <p>A confirmation email should have been sent to <strong>{order.customer_email || order.buyer_email}</strong>.</p>
                  <p className="mt-1">If you have any questions, please reply to that email or contact customer support.</p>
                </div>
                
                {/* Guest order tracking info */}
                {!authUser && order?.order_number && (
                  <div className="bg-[#FAF7F0] border border-brand-primary/20 rounded-lg p-4 mt-6">
                    <h4 className="font-semibold text-gray-700 mb-2">Save Your Order Number</h4>
                    <div className="bg-white rounded-md p-3 mb-3 text-center">
                      <p className="text-2xl font-bold text-brand-primary">{order.order_number}</p>
                    </div>
                    <p className="text-sm text-gray-600">
                      You can track your order status anytime using your order number and email address.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 w-full"
                      onClick={() => navigate("/guest-order-lookup")}
                    >
                      <Search className="mr-2 h-4 w-4" />
                      Track Your Order
                    </Button>
                  </div>
                )}
                
                <div className="mt-6 text-center">
                  <Button
                    size="lg"
                    className="bg-[#967F81] hover:bg-[#E33D16] text-white px-6 py-2 text-md rounded-md shadow-sm transition-transform hover:scale-105"
                    onClick={() => navigate("/")}
                  >
                    <HomeIcon size={18} className="mr-2"/> Continue Shopping
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </div>
      </main>
    </div>
  );
};

export default OrderConfirmation; 